import { GeoJsonLayer } from '@deck.gl/layers';
import { DataFilterExtension } from '@deck.gl/extensions';
import { useMemo } from 'react';
import type { PickingInfo } from '@deck.gl/core';
import { useOGCCollection, type GeoJsonFeature } from '../utils/ogc';
import { depthToColor } from '../utils/layer_style';
import useCurrentTimeFilter from '../utils/filtering';
import type { EventDepthDataFeature } from './event_depth_data_binsearch_dictcomp';

type ContourProps = {
  id: string | number;
  name: string;
  deployment_id: string;
  geom: any;
  start_time: Date;
  end_time: Date | null;
  max_depth_inches: number;
  contour_depth_inches: number;
};

export type PastFloodEventContourFeature = GeoJsonFeature & {
  properties: ContourProps;
};

const transformContour = (d: GeoJsonFeature) => {
  d.id = d.properties.id ?? d.properties.deployment_id;
  d.properties.start_time = new Date(d.properties.start_time);
  d.properties.end_time = d.properties.end_time ? new Date(d.properties.end_time) : null;
  return d as PastFloodEventContourFeature;
}

export const usePastFloodEventContours = (timeRange?: [Date, Date, boolean]) => {
  const from = timeRange?.[0]?.toISOString();
  const to = timeRange?.[1]?.toISOString();
  const data = useOGCCollection(
    // 'ogc.past_flood_event_contours', 
    timeRange ? 'ogc.test_event_contours' : null,
    {
    limit: 1000,
    filter: from && to ? `end_time >= '${from}' AND start_time <= '${to}'` : undefined,
  });
  data.features = useMemo(() => data.features?.map(transformContour), [data.features]);
  return data as { features: PastFloodEventContourFeature[] | undefined; loading: boolean; error: any };
}

export default function usePastFloodEventContoursLayer(
  features: PastFloodEventContourFeature[] | undefined,
  depthFeatures: EventDepthDataFeature[] | undefined, 
  { currentTime, visible = true, onClick }: { currentTime?: Date; visible?: boolean; onClick?: (info: PickingInfo<GeoJsonFeature>) => void }
) {
  const t0 = currentTime?.getTime();
  const inside = (f: PastFloodEventContourFeature) => {
    if (!t0) return 1;
    const s = f.properties.start_time?.getTime?.() ?? +new Date(f.properties.start_time);
    const e = f.properties.end_time ? (f.properties.end_time.getTime?.() ?? +new Date(f.properties.end_time)) : s;
    return (s <= t0 && t0 <= e) ? 1 : 0;
  };
  // console.log('past flood event contours features:', features);

  const flags = useCurrentTimeFilter(depthFeatures, currentTime);
  const currentDepthByDeployment = useMemo(() => {
  const map = new Map<string, number>();
  if (!depthFeatures) return map;
  depthFeatures.forEach((f, i) => {
    if (flags[i]) {
      map.set(f.properties.deployment_id, Number(f.properties.depth_inches) || 0);
    }
  });
  return map;
}, [depthFeatures, flags]);

  const fillOpacity = 120; // out of 255

  return (!features ? [] : [
    // https://deck.gl/docs/api-reference/layers/polygon-layer
    new GeoJsonLayer({
      id: 'past-flood-event-contours',
      data: { type: 'FeatureCollection', features },
      visible,
      stroked: true,
      filled: true,
      pickable: true,
      lineWidthUnits: 'pixels',
      lineWidthScale: 0,
      lineWidthMinPixels: 0,
      getLineColor: [200, 200, 240, 180],
      getFillColor: (f: PastFloodEventContourFeature) => {
        // const [r, g, b] = depthToColor(Number(f.properties.contour_depth_inches) || Number(f.properties.max_depth_inches) || 0);
        // const [r, g, b] = depthToColor(f.properties.max_depth_inches - f.properties.contour_depth_inches);
        const currentDepth = currentDepthByDeployment.get(f.properties.deployment_id) || 0;
        const contourDepth = Number(f.properties.contour_depth_inches) || 0;
        const depthDiff = currentDepth - contourDepth;
        const [r, g, b] = depthToColor(depthDiff > 0 ? depthDiff : 0);
        return [r, g, b, fillOpacity];
      },
      extensions: [new DataFilterExtension({ filterSize: 1 })],
      // getFilterValue: (f: PastFloodEventContourFeature) => inside(f),
      getFilterValue: (f: PastFloodEventContourFeature) => {
        const currentDepth = currentDepthByDeployment.get(f.properties.deployment_id) || 0;
        return (currentDepth >= (Number(f.properties.contour_depth_inches) || 0)) ? 1 : 0;
      },
      filterRange: [1, 1],
      onClick,
      parameters: { depthTest: false },
      updateTriggers: {
        // getFillColor: [currentTime, depthFeatures],
        getFilterValue: [currentTime, depthFeatures],
      },
    }),
  ]);
}

